
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Trash2 } from "lucide-react";

export const AdminUsers = () => {
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all admin users with their profile information
  const { data: adminUsers, isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      console.log('Fetching admin users...');
      const { data, error } = await supabase
        .from('admin_users')
        .select('*');
      
      if (error) {
        console.error('Error fetching admin users:', error);
        throw error;
      }

      console.log('Admin users data:', data);

      // Get profile info for each admin user
      const adminUsersWithProfiles = await Promise.all(
        data.map(async (admin) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('email, first_name, last_name')
            .eq('id', admin.user_id)
            .single();
          
          return {
            ...admin,
            profiles: profile
          };
        })
      );
      
      return adminUsersWithProfiles;
    },
  });

  // Create admin user mutation
  const createAdminMutation = useMutation({
    mutationFn: async (email: string) => {
      console.log('Creating admin for email:', email);
      
      // First check if a profile exists with this email
      const { data: existingProfile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', email)
        .single();

      console.log('Profile lookup result:', { existingProfile, profileError });

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('Profile error:', profileError);
        throw new Error('Error checking for existing user');
      }

      if (!existingProfile) {
        throw new Error('No user found with this email. The user must sign up first.');
      }

      console.log('Found user profile, creating admin entry for user_id:', existingProfile.id);

      const { error } = await supabase
        .from('admin_users')
        .insert({
          user_id: existingProfile.id,
          role: 'admin'
        });

      if (error) {
        console.error('Admin creation error:', error);
        throw error;
      }

      console.log('Admin user created successfully');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-check'] });
      setNewAdminEmail('');
      toast({
        title: "Success",
        description: "Admin user created successfully.",
      });
    },
    onError: (error) => {
      console.error('Error creating admin:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create admin user.",
        variant: "destructive",
      });
    },
  });

  // Delete admin user mutation
  const deleteAdminMutation = useMutation({
    mutationFn: async (adminId: string) => {
      const { error } = await supabase
        .from('admin_users')
        .delete()
        .eq('id', adminId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-check'] });
      toast({
        title: "Success",
        description: "Admin user removed successfully.",
      });
    },
    onError: (error) => {
      console.error('Error deleting admin:', error);
      toast({
        title: "Error",
        description: "Failed to remove admin user.",
        variant: "destructive",
      });
    },
  });

  const handleCreateAdmin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAdminEmail) {
      toast({
        title: "Error",
        description: "Please enter an email address.",
        variant: "destructive",
      });
      return;
    }
    createAdminMutation.mutate(newAdminEmail);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Add New Admin User</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateAdmin} className="space-y-4">
            <div>
              <Label htmlFor="adminEmail">User Email</Label>
              <Input
                id="adminEmail"
                type="email"
                placeholder="admin@example.com"
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                required
              />
              <p className="text-sm text-muted-foreground mt-1">
                The user must have signed up first before they can be made an admin.
              </p>
            </div>

            <Button type="submit" disabled={createAdminMutation.isPending}>
              {createAdminMutation.isPending ? "Adding..." : "Add Admin"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Existing Admin Users</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p>Loading admin users...</p>
          ) : (
            <div className="space-y-2">
              {adminUsers?.map((admin) => (
                <div key={admin.id} className="flex items-center justify-between p-3 border rounded">
                  <div>
                    <p className="font-medium">
                      {admin.profiles?.email || `User ID: ${admin.user_id}`}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {admin.profiles?.first_name && admin.profiles?.last_name 
                        ? `${admin.profiles.first_name} ${admin.profiles.last_name} • ` 
                        : ''}
                      Role: {admin.role}
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => deleteAdminMutation.mutate(admin.id)}
                    disabled={deleteAdminMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {adminUsers?.length === 0 && (
                <p className="text-muted-foreground">No admin users found.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
